import {users as usersShared} from '@cl/shared';
import {CampaignObjective} from 'common/src/modules/campaigns/copilot/copilotCampaigns.enums';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {AgentName} from 'common/src/modules/shared/copilot/copilot.enums';
import {Agent, ConversationStarter} from 'common/src/modules/shared/copilot/copilot.types';
import agent from 'common/src/modules/shared/copilot/utils/creators/agent';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {CLStore} from 'common/src/redux/store';
import addRandomKeyToObjectArray from 'common/src/utils/arrayUtils/addRandomKeyToObjectArray';
import campaignContext from '../../../../campaigns/copilot/contexts/campaignContext';
import {inlineWidgetSchema} from '../../copilotAnalytics.schemas';
import getBestPracticesTool from '../../tools/getBestPracticesTool';
import {CampaignAnalyticsToolName} from './campaignAnalyticsAgent.enums';
import icons from './campaignAnalyticsAgent.icons';
import campaignAnalyticsContext from './contexts/campaignAnalyticsContext';
import checkWidgetExistsTool from './tools/checkWidgetExistsTool';
import createDashboardTool from './tools/createDashboardTool';
import createWidgetTool from './tools/createWidgetTool';
import deleteDashboardTool from './tools/deleteDashboardTool';
import deleteWidgetTool from './tools/deleteWidgetFromDashboardTool';
import getCampaignAnalyticsDataTool from './tools/getCampaignAnalyticsDataTool';
import getCampaignAnalyticsTableDataTool from './tools/getCampaignAnalyticsTableDataTool';
import getCampaignTopPerformingTool from './tools/getCampaignTopPerformingTool';
import getCampaignWorstPerformingTool from './tools/getCampaignWorstPerformingTool';
import highlightWidgetTool from './tools/highlightWidgetTool';
import navigateToCampaignInsightsTool from './tools/navigateToCampaignInsightsTool';
import openCampaignDashboardTool from './tools/openCampaignDashboardTool';
import renameDashboardTool from './tools/renameDashboardTool';
import supportedFilterValuesTool from './tools/supportedFilterValuesTool';
import updateWidgetTool from './tools/updateWidgetTool';
import nodeFallbackAnalyzerTool from './tools/nodeFallbackAnalyzerTool';
import persistInlineWidgetToCampaignDashboardTool from './tools/persistInlineWidgetToCampaignDashboardTool';
import {KnowledgeRagTopic} from 'common/src/modules/ai/ai.enums';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';

function campaignAnalyticsAgent(store: CLStore): Agent {
  const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);
  const conversationStarters: ConversationStarter[] = addRandomKeyToObjectArray([
    {
      displayName: 'Supported dimensions',
      prompt: 'Show supported dimensions for this campaign',
      icon: {
        src: icons.supportedDimensions,
        color: '#9100B4',
      },
    },
    {
      displayName: 'Campaign KPIs',
      prompt: 'Which KPIs should I track for this campaign?',
      icon: {
        src: icons.campaignKPIs,
        color: '#F46036',
      },
    },
    {
      displayName: 'Impressions over time',
      prompt: 'Show a chart of campaign impressions over the past 7 days',
      icon: {
        src: icons.impressionsOverTime,
        color: '#25A18E',
      },
    },
    {
      displayName: 'Top performing creatives',
      prompt: 'Show top creatives by impressions in the past 7 days',
      icon: {
        src: icons.topPerforming,
        color: '#2C7BE5',
      },
    },
  ]);

  const role =
    'You are a campaign-specific analytics agent who fetches and analyzes campaign data, and keeps dashboards relevant by adding, updating, or removing widgets as needed';

  const inScope = [
    'Answer questions about campaign analytics, metrics and dimensions',
    'Converse specific campaign analytics tasks and questions',
    'Managing campaign level dashboards and widgets',
    'Handle analytics requests only when the user is within a specific campaign context (e.g., Campaign Insights page)',
  ];

  const outOfScope = ['Anything related to account level analytics'];

  const userHasAnalyticsWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.AnalyticsWrite,
  );

  const instructions = `
    ***Answer only if campaign was active***
    
    1. Prerequisite:  
      a. In order to fetch analytics data for a campaign, you need to be on the campaign page, use ${CampaignAnalyticsToolName.NavigateToCampaignInsights} tool to navigate to the campaign insights page.
      b. If campaign id is not provided, ask the user to provide it.

    2. How to fetch Analytics data:
        - Date range: If the user requested a specific one use it, otherwise, take the dates range from the campaign context (up to 7 days)
        - Metrics: only choose from the "Available metrics" list (don't invent your own metrics they will not be supported)
        - Viewing dimensions: only choose from the "Available viewing dimensions" list
        - Filtering dimensions (A filter is not mandatory, but if the user requested one, you must):
            - only choose a dimension from the "Available filtering dimensions" list
            - only choose values from allowed values for that dimension (Never assume that a value is valid, even if it is common)
            - if the requested value is not in the allowed list, do not apply the filter. Instead, return the list of valid options to the user and request clarification.

    3. When asked about anything related to campaign KPIs (e.g. "Is my campaign performing well"):
        - first find out the campaign objective: ${CampaignObjective}
        - second refer to the "Best Practices" for industry benchmarks and metric rate comparisons towards these benchmarks.
        
    4. How to answer user queries:
        - First respond with a textual answer which includes the dates range and the name of the requested query.
        - Then show an "inline widget" when possible in this manner:
            - When the user requests a specific widget type, generate that widget if it is technically supported. Before generating it, check the widget's known limitations. If the requested widget is not supported because of a limitation, clearly explain which limitation applies and provide a valid alternative configuration.
            - A title which contains metrics and dimensions. The number of metrics in the title MUST NEVER be greater than 2.
            - Analytics data must be taken ONLY from the result of the ${CampaignAnalyticsToolName.GetCampaignAnalyticsData} or ${CampaignAnalyticsToolName.GetCampaignTopPerforming} tool. NEVER add any other keys!
            - The "inline widget"
            ${!isSkillsMode && userHasAnalyticsWriteRole ? `- A suggestion to add this "inline widget" into a campaign dashboard (This suggestion should always come AFTER the widget )` : ``}

    5. How to add an "inline widget" to a campaign dashboard:
      - first check if a dashboard widget like that already exists, if it does, just highlight it
      - if it doesn't, ask the user weather to create a new dashboard and add the widget to it OR to add the widget to an existing **NOT read only dashboard**
      - then highlight the dashboard widget.

    6. For anything related to node fallback analysis, use the ${CampaignAnalyticsToolName.AnalyzeNodeFallback} tool.
      a. Use only the node id and date range, that was requested by the user, not the name.
  `;

  const campaignDashboardManagmentTools = [
    // Dashboard tools
    createDashboardTool(store),
    renameDashboardTool(store),
    deleteDashboardTool(store),

    // Widget tools
    createWidgetTool(store),
    updateWidgetTool(store),
    deleteWidgetTool(store),
  ];

  const tools = [
    navigateToCampaignInsightsTool(store),

    // Data fetching tools
    getCampaignTopPerformingTool(store),
    getCampaignAnalyticsDataTool(store),
    getCampaignAnalyticsTableDataTool(store),
    getCampaignWorstPerformingTool(store),

    getBestPracticesTool(),
    supportedFilterValuesTool(store),

    // Widget tools
    checkWidgetExistsTool(store),
    highlightWidgetTool(store),
    openCampaignDashboardTool(store),
    nodeFallbackAnalyzerTool(store),

    ...(isSkillsMode ? [persistInlineWidgetToCampaignDashboardTool(store)] : []),

    ...(userHasAnalyticsWriteRole ? campaignDashboardManagmentTools : []),
  ];

  const contexts = [campaignContext(store), campaignAnalyticsContext(store)];

  return agent({
    name: AgentName.CampaignAnalytics,
    role,
    store,
    conversationStarters,
    inScope,
    outOfScope,
    instructions,
    tools,
    widgetSchemas: [inlineWidgetSchema],
    contexts,
    knowledgeTopics: [KnowledgeRagTopic.CampaignAnalyticsAgent],
  });
}

export default campaignAnalyticsAgent;
